import {useCallback, useEffect, useState} from "react";

interface UseCollapseByIdParams<T> {
  /** The current list of items being displayed. Used by `handleExpandAll` and to auto-collapse on data change. */
  items: T[] | undefined;
  /** Extracts the unique id from an item. Defaults to `item.id`. */
  getItemId?: (item: T) => string;
}

interface UseCollapseById {
  /** Source of truth — every id present in this Set is considered expanded. */
  expandedIds: Set<string>;
  /** O(1) membership check. */
  isExpanded: (id: string) => boolean;
  /** Toggle a single item's expanded state. */
  handleToggle: (id: string) => void;
  /** Expand every item currently in `items`. */
  handleExpandAll: () => void;
  /** Collapse everything. Wired to `ReportDataSummary.setExpandedAll`. */
  setExpandedAll: () => void;
  /**
   * Compatibility shim for `ReportDataSummary.setExpandedItems`.
   * An empty object collapses all; a populated object expands the keys provided.
   */
  setExpandedItems: (items: Record<string, unknown>) => void;
  /** True when at least one item is expanded. */
  hasExpandedItems: boolean;
}

/**
 * Hook for managing expand/collapse state when items have unique string ids.
 *
 * Uses a `Set<string>` internally — membership equals expanded.
 *
 * @see {@link useReportDataSummaryCollapseByIndex} for the indexed (no-id) variant.
 */
const useCollapseById = <T>({
  items,
  getItemId = (item: T) => (item as unknown as { id: string }).id,
}: UseCollapseByIdParams<T>): UseCollapseById => {
  const [expandedIds, setExpandedIdsState] = useState<Set<string>>(new Set());

  // Auto-collapse when the items reference changes (e.g. after a data refresh).
  useEffect(() => {
    setExpandedIdsState(new Set());
  }, [items]);

  const handleToggle = useCallback((id: string) => {
    setExpandedIdsState(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    if (!items?.length) return;
    setExpandedIdsState(new Set(items.map(getItemId)));
  }, [items, getItemId]);

  const setExpandedAll = useCallback(() => {
    setExpandedIdsState(new Set());
  }, []);

  const setExpandedItems = useCallback((nextItems: Record<string, unknown>) => {
    setExpandedIdsState(new Set(Object.keys(nextItems)));
  }, []);

  const isExpanded = useCallback((id: string) => expandedIds.has(id), [expandedIds]);

  const hasExpandedItems = expandedIds.size > 0;

  return {
    expandedIds,
    isExpanded,
    handleToggle,
    handleExpandAll,
    setExpandedAll,
    setExpandedItems,
    hasExpandedItems,
  } as UseCollapseById;
}

export {useCollapseById};

